import { MaterialSymbol } from "@material-symbols/font-400";
import type { TicketDepartment, TicketStatus } from "../services/investor/ticketService";

/** Who asks: owners and tenants pick from different topics. */
export type TicketAudience = "investor" | "tenant";

const TICKET_DEPARTMENTS: TicketDepartment[] = [
  "finance",
  "property_management",
  "legal_handover",
  "technical",
];

/** The same areas and topics the team files tickets under in the employee portal. */
const INVESTOR_CATEGORIES: Record<TicketDepartment, string[]> = {
  finance: ["payout", "invoice", "service_charge", "bank_details", "statement", "afa_report", "finance_other"],
  property_management: [
    "leasing",
    "tenant",
    "lease_renewal",
    "maintenance",
    "key_handover",
    "viewing_request",
    "property_management_other",
  ],
  legal_handover: [
    "handover",
    "power_of_attorney",
    "snagging",
    "title_deed",
    "resale",
    "dewa",
    "empower",
    "legal_case",
    "pm_contract",
    "legal_handover_other",
  ],
  technical: ["bug", "feedback", "credentials", "data_entry", "connection_request"],
};

// Mirrors TENANT_TICKET_CATEGORIES in the APIs. Repairs are not here: tenants
// report those through the maintenance request on their dashboard.
const TENANT_CATEGORIES: Partial<Record<TicketDepartment, string[]>> = {
  finance: ["rent_payment", "security_deposit", "tenant_invoice", "finance_other"],
  property_management: [
    "move_in",
    "move_out",
    "lease_renewal",
    "ejari",
    "utilities",
    "building_access",
    "tenancy_contract",
    "property_management_other",
  ],
};

export interface TicketTaxonomy {
  audience: TicketAudience;
  departments: TicketDepartment[];
  categories: (department: TicketDepartment) => string[];
  /** The things this audience asks most, as chips under the question box. */
  quickTopics: string[];
}

export const ticketTaxonomy = (audience: TicketAudience): TicketTaxonomy => {
  const byDepartment = audience === "tenant" ? TENANT_CATEGORIES : INVESTOR_CATEGORIES;
  return {
    audience,
    departments: TICKET_DEPARTMENTS.filter((department) => (byDepartment[department] ?? []).length > 0),
    categories: (department) => byDepartment[department] ?? [],
    quickTopics:
      audience === "tenant"
        ? ["rent_payment", "security_deposit", "move_out", "lease_renewal"]
        : ["payout", "invoice", "service_charge", "maintenance", "tenant"],
  };
};

export const DEPARTMENT_ICONS: Record<TicketDepartment, MaterialSymbol> = {
  finance: "payments",
  property_management: "home_work",
  legal_handover: "contract",
  technical: "devices",
};

const CATEGORY_ICONS: Partial<Record<string, MaterialSymbol>> = {
  payout: "account_balance_wallet",
  invoice: "receipt_long",
  service_charge: "apartment",
  bank_details: "account_balance",
  statement: "summarize",
  afa_report: "analytics",
  finance_other: "payments",
  leasing: "real_estate_agent",
  tenant: "group",
  lease_renewal: "autorenew",
  maintenance: "handyman",
  key_handover: "key",
  viewing_request: "event",
  property_management_other: "home_work",
  handover: "handshake",
  power_of_attorney: "gavel",
  snagging: "construction",
  title_deed: "verified",
  resale: "sell",
  dewa: "water_ec",
  empower: "ac_unit",
  legal_case: "balance",
  pm_contract: "contract",
  legal_handover_other: "folder",
  bug: "bug_report",
  feedback: "chat",
  credentials: "lock",
  data_entry: "person",
  connection_request: "wifi",
  rent_payment: "payments",
  security_deposit: "savings",
  tenant_invoice: "receipt_long",
  move_in: "key",
  move_out: "logout",
  ejari: "description",
  utilities: "bolt",
  building_access: "apartment",
  tenancy_contract: "contract",
};

/**
 * Words that point to a topic, German and English, for the suggestions under
 * the question box. The "other" topics have none on purpose: they are picked,
 * never suggested.
 */
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  payout: ["auszahlung", "ausgezahlt", "überweisung", "zahlung", "scheck", "miete", "payout", "transfer", "cheque", "paid out", "payment", "rent", "rental income"],
  invoice: ["rechnung", "inv", "gebühr", "betrag", "differenz", "invoice", "fee", "charge"],
  service_charge: ["service charge", "servicegebühr", "hausgeld", "community", "nebenkosten"],
  bank_details: ["bankverbindung", "bank", "iban", "swift", "konto", "account number"],
  statement: ["abrechnung", "kontoauszug", "jahresabrechnung", "steuer", "statement", "tax"],
  afa_report: ["afa", "gutachten", "abschreibung", "bewertung", "appraisal", "valuation"],
  leasing: ["vermietung", "vermieten", "leerstand", "neuer mieter", "letting", "vacant", "vacancy", "rent", "rent out", "new tenant"],
  tenant: ["mieter", "miete", "mietvertrag", "kündigung", "auszug", "tenant", "tenancy", "lease", "rent", "notice", "evict", "move out"],
  // Next term's rent too: tenants ask for a lower rent in rent words, which alone would point to rent payment.
  lease_renewal: [
    "verlängerung", "verlängern", "mieterhöhung", "läuft aus", "ablauf", "ejari",
    "mietminderung", "miete senken", "gesenkt", "reduzieren", "reduziert", "neue miete", "nächstes jahr", "nächstem jahr", "verhandeln",
    "lease", "renewal", "renew", "extend", "extension", "expire", "expiry", "rent increase",
    "reduce", "reduction", "lowered", "lower the rent", "lower rent", "decrease", "negotiate", "negotiation", "new rent", "next year",
  ],
  maintenance: ["wartung", "reparatur", "defekt", "kaputt", "tropft", "klima", "schaden", "handwerker", "maintenance", "repair", "broken", "leak", "damage", "fix", "plumbing", "air condition"],
  key_handover: ["schlüssel", "zugangskarte", "key", "keys", "access card"],
  viewing_request: ["besichtigung", "termin", "viewing", "visit"],
  handover: ["übergabe", "handover"],
  snagging: ["snagging", "de-snagging", "desnagging", "abnahme", "mängel", "mängelliste", "snag", "defects", "inspection"],
  power_of_attorney: ["vollmacht", "power of attorney", "poa"],
  title_deed: ["title deed", "oqood", "grundbuch", "eigentumsnachweis"],
  resale: ["weiterverkauf", "verkauf", "verkaufen", "resale", "sell"],
  dewa: ["dewa", "strom", "wasser", "electricity", "water"],
  empower: ["empower", "kühlung", "chiller", "cooling"],
  legal_case: ["rechtsfall", "anwalt", "klage", "gericht", "lawyer", "court", "legal"],
  pm_contract: ["verwaltungsvertrag", "paket", "premium", "basic", "management contract", "package"],
  bug: ["fehler", "funktioniert nicht", "absturz", "bug", "error", "crash"],
  feedback: ["feedback", "vorschlag", "idee", "suggestion"],
  credentials: ["passwort", "anmeldung", "zugangsdaten", "password", "login", "sign in", "2fa"],
  data_entry: ["meine daten", "adresse", "telefon", "stammdaten", "address", "phone", "my details"],
  connection_request: ["anschluss", "internet", "wlan", "wifi", "connection"],
  rent_payment: ["miete", "mietzahlung", "scheck", "zahlung", "überweisung", "rent", "payment", "pay", "cheque", "transfer", "bounced"],
  security_deposit: ["kaution", "rückzahlung", "deposit", "security deposit", "refund"],
  tenant_invoice: ["rechnung", "gebühr", "strafe", "invoice", "fee", "charge", "penalty"],
  move_in: ["einzug", "schlüssel", "zugangskarte", "move in", "key", "keys", "access card", "check in"],
  move_out: ["auszug", "kündigung", "kündigen", "move out", "moving out", "notice", "terminate", "termination"],
  ejari: ["ejari", "dokument", "registrierung", "document", "registration"],
  utilities: ["dewa", "strom", "wasser", "chiller", "empower", "kühlung", "internet", "electricity", "water", "cooling", "utility", "bill"],
  building_access: ["parkplatz", "parken", "zugang", "aufzug", "nachbar", "lärm", "parking", "access", "elevator", "lift", "neighbour", "noise", "pool", "gym"],
  tenancy_contract: ["mietvertrag", "vertrag", "vertragsfrage", "klausel", "contract", "tenancy contract", "lease agreement", "clause"],
};

export const departmentOf = (category: string): TicketDepartment | null =>
  TICKET_DEPARTMENTS.find(
    (department) =>
      INVESTOR_CATEGORIES[department].includes(category) ||
      (TENANT_CATEGORIES[department] ?? []).includes(category)
  ) ?? null;

export interface TopicMatch {
  /** Set when only an area matched, so the investor still has to pick its topic. */
  department: TicketDepartment | null;
  categories: string[];
}

const words = (text: string): string[] => text.toLowerCase().split(/[^a-z0-9äöüß]+/).filter(Boolean);

// Short words only count whole, so "fee" stays out of "feedback"; longer ones
// also inside German compounds like "Stromrechnung".
const containsTerm = (term: string, query: string, typed: string[]): boolean =>
  term.length < 5 ? typed.some((word) => word === term || word === `${term}s`) : query.includes(term);

// One letter wrong, missing, extra or swapped with its neighbour.
const oneTypoApart = (a: string, b: string): boolean => {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && a[i] === b[i]) i++;
  const [x, y] = [a.slice(i), b.slice(i)];
  return (
    x.slice(1) === y.slice(1) ||
    x.slice(1) === y ||
    x === y.slice(1) ||
    (x[0] === y[1] && x[1] === y[0] && x.slice(2) === y.slice(2))
  );
};

/**
 * The whole term scores its length. Otherwise the end of what was typed may be
 * the start of the term ("lease re", "serv"), or one word may be the term with
 * a typo. Typos need the first letter right, or "getting" would find "letting".
 */
const termScore = (term: string, query: string, typed: string[]): number => {
  if (containsTerm(term, query, typed)) return term.length;
  for (let from = 0; from < typed.length; from++) {
    const tail = typed.slice(from).join(" ");
    if (tail.length >= 3 && term.startsWith(tail)) return tail.length;
  }
  const typo =
    term.length >= 6 &&
    !term.includes(" ") &&
    typed.some((word) => word[0] === term[0] && oneTypoApart(word, term));
  return typo ? term.length - 1 : 0;
};

/**
 * Ranks the audience's topics by the words typed, while they type. A topic's
 * own name counts extra. Without a topic hit, an area's name brings up that
 * area's topics.
 */
export const matchTopics = (
  text: string,
  categoryLabel: (category: string) => string,
  departmentLabel: (department: TicketDepartment) => string,
  taxonomy: TicketTaxonomy
): TopicMatch => {
  const typed = words(text);
  const query = typed.join(" ");
  if (query.length < 2) return { department: null, categories: [] };

  const hits: { category: string; score: number }[] = [];
  taxonomy.departments.forEach((department) =>
    taxonomy.categories(department).forEach((category) => {
      if (category.endsWith("_other")) return;
      const label = words(categoryLabel(category)).join(" ");
      let score = containsTerm(label, query, typed) ? 20 : 0;
      new Set([...(CATEGORY_KEYWORDS[category] ?? []), label]).forEach((term) => {
        score += termScore(term, query, typed);
      });
      if (score > 0) hits.push({ category, score });
    })
  );
  if (hits.length > 0) {
    return {
      department: null,
      categories: hits.sort((a, b) => b.score - a.score).slice(0, 4).map((hit) => hit.category),
    };
  }

  const area = taxonomy.departments.find((department) =>
    containsTerm(words(departmentLabel(department)).join(" "), query, typed)
  );
  return area
    ? { department: area, categories: taxonomy.categories(area) }
    : { department: null, categories: [] };
};

export const ticketIcon = (category: string, department: TicketDepartment): MaterialSymbol =>
  CATEGORY_ICONS[category] ?? DEPARTMENT_ICONS[department] ?? "support_agent";

export type TicketStatusTone = "received" | "progress" | "waiting" | "done";

export const TICKET_STATUS_TONES: Record<TicketStatus, TicketStatusTone> = {
  open: "received",
  in_progress: "progress",
  waiting_on_investor: "waiting",
  resolved: "done",
};

/**
 * What the investor sees: three states. Waiting for them is still "in
 * progress" to them; a new reply from the team is an unread signal on top.
 */
export type SupportStatus = "received" | "progress" | "done";

// The team rarely moves a ticket off "open" by hand, so their first public
// reply is what turns it into "in progress" for the investor.
export const supportStatus = (status: TicketStatus, teamReplied = false): SupportStatus =>
  status === "resolved" ? "done" : status === "open" && !teamReplied ? "received" : "progress";

/** The request number investors may quote to the team; the ticket id, padded. */
export const requestNumber = (ticketId: number): string => `ANF-${String(ticketId).padStart(4, "0")}`;

export const unitTitle = (unit: { unit_number: string; property_name: string | null } | null): string | null =>
  unit ? [unit.unit_number, unit.property_name].filter(Boolean).join(" · ") : null;

/** One line of a message, cut at a word and marked as cut. */
export const firstLine = (text: string, max: number): string => {
  const line = text.replace(/\s+/g, " ").trim();
  if (line.length <= max) return line;
  const cut = line.slice(0, max);
  return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), max - 12)).replace(/[,;:.!?-]$/, "")} …`;
};

const locale = (language: string) => (language === "de" ? "de-DE" : "en-GB");
const pad = (value: number) => String(value).padStart(2, "0");

/** 25.09. in German, 25 Sept in English. */
export const formatShortDate = (iso: string, language: string): string => {
  const date = new Date(iso);
  return language === "de"
    ? `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.`
    : date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

export const formatTime = (iso: string): string => {
  const date = new Date(iso);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** A day separator in the phone conversation, e.g. "So., 27.09". */
export const formatDay = (iso: string, language: string): string =>
  new Date(iso)
    .toLocaleDateString(locale(language), { weekday: "short", day: "2-digit", month: "2-digit" })
    .replace(/\.$/, "");

/** Uploads are stored as "<timestamp>-<name>"; the investor only needs the name. */
export const attachmentName = (fileName: string): string => fileName.replace(/^\d{13}-/, "");

// A date is a value, formatted here and never baked into a translation.
export const formatTicketDate = (iso: string, language: string, withTime = false): string =>
  new Date(iso).toLocaleString(language === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;
export const ATTACHMENT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

export const isAllowedAttachment = (file: File): boolean =>
  ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"].includes(file.type);
